import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const DEFAULTS = {
  hero_stat_tuition: '$600+ Tuition/Sem',
  hero_stat_visa_assistance: '100% Visa Assistance',
  hero_stat_students_placed: '1,000+ Students Placed',
};

export default function Hero() {
  const [stats, setStats] = useState(DEFAULTS);

  useEffect(() => {
    fetch(`${API_BASE}/settings/public`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) setStats({ ...DEFAULTS, ...json.data });
      })
      .catch(() => {});
  }, []);

  const splitStat = (text) => {
    const [value, ...rest] = text.split(' ');
    return { value, label: rest.join(' ') };
  };

  const placedStat = splitStat(stats.hero_stat_students_placed);
  const visaStat = splitStat(stats.hero_stat_visa_assistance);
  const tuitionStat = splitStat(stats.hero_stat_tuition);

  return (
    <section className="relative pt-32 pb-24 md:pt-28 md:pb-32 overflow-hidden">
      {/* Decorative dotted pattern, top-left 
      <div
        className="absolute top-24 left-4 md:left-12 w-32 h-32 opacity-30 pointer-events-none hidden md:block"
        style={{ backgroundImage: 'radial-gradient(#1e3c72 1.5px, transparent 1.5px)', backgroundSize: '14px 14px' }}
      />*/}

      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #ff7b7b 100%)' }} />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-16 items-center relative">
        {/* Left: Text content */}
        <div className="z-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8fb3ff]/20 text-[#174388] font-bold text-xs uppercase tracking-wider mb-6">
            <span className="w-2 h-2 rounded-full bg-[#ff7b7b] animate-pulse" />
            Trusted by {placedStat.value} Students
          </span>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#00255a] leading-[1.1]">
            Scholarships & Study{' '}
            <span className="relative inline-block">
              <span className="relative z-10">Abroad</span>
              <svg className="absolute -bottom-2 left-0 w-full h-4" viewBox="0 0 200 20" preserveAspectRatio="none">
                <path d="M2,15 Q50,2 100,10 T198,8" fill="none" stroke="#ff7b7b" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>{' '}
            Made Clear
          </h1>

          <p className="text-lg text-[#64748b] mb-10 max-w-lg leading-relaxed">
            Find international courses, scholarship opportunities and expert application support for your study or work journey abroad.
            We help ambitious students and professionals turn global opportunities into practical next steps.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12">
            <Link
              to="/apply"
              className="text-white px-8 py-4 rounded-xl font-bold flex items-center gap-2 hover:scale-105 transition-all shadow-lg"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Start Your Study Abroad Application
              <span className="material-symbols-outlined">arrow_forward</span>
            </Link>
            <Link
              to="/openings"
              className="flex items-center gap-3 group"
            >
              <span className="w-12 h-12 rounded-full border-2 border-[#00255a]/20 flex items-center justify-center text-[#00255a] group-hover:bg-[#00255a] group-hover:text-white transition-all">
                <span className="material-symbols-outlined">play_arrow</span>
              </span>
              <span className="font-bold text-[#00255a] underline underline-offset-4">Explore Study Opportunities</span>
            </Link>
          </div>

          {/* Small inline trust row */}
          <div className="flex items-center gap-6 text-sm text-[#64748b]">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[#ff7b7b] text-lg">verified</span>
              {visaStat.value} {visaStat.label}
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[#ff7b7b] text-lg">payments</span>
              {tuitionStat.value} {tuitionStat.label}
            </div>
          </div>
        </div>

        {/* Right: Photo with clean circle background (MediCloud-style) */}
        <div className="relative flex justify-center md:justify-end">
          {/* Soft circle background behind the cutout photo */}
          <div
            className="absolute top-1/2 right-0 md:right-8 -translate-y-1/2 w-[380px] h-[380px] md:w-[440px] md:h-[440px] rounded-full"
            style={{ background: 'radial-gradient(circle, #8fb3ff33 0%, #ff7b7b1a 70%, transparent 100%)' }}
          />

          <img
            className="relative z-10 w-full max-w-[420px] h-auto object-contain"
            alt="Student exploring scholarships and study abroad opportunities with IASC"
            src="/images/scholarship-student-nobg.png"
          />

          {/* Floating stat card */}
          <div className="absolute bottom-4 left-0 md:left-4 z-20 bg-white rounded-2xl shadow-xl border border-[#e2e8f0] p-5 flex items-center gap-4 max-w-[240px]">
            <div className="flex -space-x-3">
              {['A', 'B', 'C'].map((letter) => (
                <div key={letter} className="w-9 h-9 rounded-full bg-[#8fb3ff] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                  {letter}
                </div>
              ))}
            </div>
            <div>
              <p className="text-xl font-bold text-[#00255a] leading-none">{placedStat.value}</p>
              <p className="text-[11px] text-[#64748b] uppercase tracking-wide mt-1">{placedStat.label}</p>
            </div>
          </div>

          {/* Floating feature pills */}
          <div className="absolute top-8 right-0 md:right-4 z-20 bg-white rounded-xl shadow-lg border border-[#e2e8f0] px-4 py-3 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#00255a] text-xl">verified_user</span>
            <span className="text-xs font-bold text-[#00255a] whitespace-nowrap">{visaStat.value} {visaStat.label}</span>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 left-0 md:-left-4 z-20 bg-[#00255a] text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-2 hidden md:flex">
            <span className="material-symbols-outlined text-[#ff8483] text-xl">payments</span>
            <span className="text-xs font-bold whitespace-nowrap">{tuitionStat.value} {tuitionStat.label}</span>
          </div>
        </div>
      </div>

      {/* Bottom decorative wave */}
      <div className="absolute bottom-0 right-0 w-64 h-64 opacity-10 pointer-events-none rounded-tl-[100px]"
        style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #ff7b7b 100%)' }} />
    </section>
  );
}
